import { hashHistory } from 'react-router';
import queryString from 'query-string';
import _unset from 'lodash/unset';
import _split from 'lodash/split';
import _reject from 'lodash/reject';
import _compact from 'lodash/compact';

export default class UrlUtils {
  /* ----------------- */
  /* removeQueryParams */
  /* ----------------- */
  /**
   * Removes specified query parameters from the current URL's query string and updates the browser's history.
   *
   * @param {Object} query - The current URL's query parameters as an object.
   * @param {string} pathname - The pathname to which the history should be updated.
   * @param {string[]} paramsToRemove - An array of query parameter names to be removed.
   */
  static removeQueryParams(query, pathname, paramsToRemove) {
    paramsToRemove.forEach((paramToRemove) => _unset(query, paramToRemove));
    hashHistory.replace({
      pathname,
      search: `?${queryString.stringify(query, { encode: true })}`,
    });
  }

  /* ---------- */
  /* openWindow */
  /* ---------- */
  static openWindow(uri, target = '_self') { window.open(uri, target); }

  /* ----------- */
  /* generateUrl */
  /* ----------- */
  static generateUrl({
    domain,
    port,
    pathname,
    fragment,
  }) {
  // ❌ Early return: no domain is provided
    if (!domain) {
      throw new Error('Domain is required to generate a URL');
    }

    let uri;

    if (process.env.NODE_ENV === 'development') {
      uri = new URL(window.location.origin);
      uri.port = port || '';
    } else {
      uri = new URL(`https://${domain}`);
    }

    uri.pathname = pathname || '';
    uri.hash = fragment || '';

    return uri.toString();
  }

  /* ------------------- */
  /* openCpmClientWindow */
  /* ------------------- */
  static openCpmClientWindow(domain, pathname, fragment) {
    const uriString = UrlUtils.generateUrl({
      domain,
      port: '8002',
      pathname,
      fragment,
    });
    UrlUtils.openWindow(uriString, '_blank');
  }

  /* ------------------ */
  /* openDownloadWindow */
  /* ------------------ */
  static openDownloadWindow(uri) {
    const uriObject = new URL(uri);
    uriObject.searchParams.append('redirect_uri', window.location.origin);
    UrlUtils.openWindow(uriObject.toString(), '_blank');
  }

  static downloadFile(fileUrl, filename = '') {
    try {
      // Validate the URL
      if (!fileUrl || typeof fileUrl !== 'string') {
        throw new Error('Invalid file URL');
      }

      // Create and validate the URL
      const url = new URL(fileUrl, window.location.origin);

      // Basic security check - ensure the protocol is allowed
      if (!url.protocol.startsWith('http')) {
        throw new Error('Unauthorized protocol');
      }

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url.toString();
      link.download = filename;
      link.style.display = 'none';

      // Append the link temporarily to the DOM
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while downloading the file:', error);
      return false;
    } finally {
      // Ensure the link is removed from the DOM even if an error occurs
      const existingLink = document.querySelector(`a[href="${fileUrl}"]`);
      if (existingLink) {
        document.body.removeChild(existingLink);
      }
    }
  }

  /* --------------------------- */
  /* redirectToCpmClientLocation */
  /* --------------------------- */
  static redirectToCpmClientLocation(domain, pathname, fragment) {
    window.location.href = UrlUtils.generateUrl({
      domain,
      port: '8002',
      pathname,
      fragment,
    });
  }

  /* --------------------------- */
  /* redirectToCpmManagerLocation */
  /* --------------------------- */
  static redirectToCpmManagerLocation(domain, pathname, fragment) {
    window.location.href = UrlUtils.generateUrl({
      domain,
      port: '8001',
      pathname,
      fragment,
    });
  }

  /* ----------------------- */
  /* Clean URL               */
  /* ----------------------- */
  static cleanUrl(windowLocation) {
    const url = windowLocation.origin + windowLocation.pathname + _split(windowLocation.hash, '?')[0];
    window.history.replaceState({}, document.title, url);
  }

  /* ----------------------- */
  /* Remove parking ID       */
  /* ----------------------- */
  static removeParkingId(windowLocation) {
    return _reject(
      _compact(_split(windowLocation.pathname, '/')),
      (segment) => /^\d+$/.test(segment),
    );
  }
}



// WEBPACK FOOTER //
// ./utils/UrlUtils.js